import { useOne } from "@refinedev/core";
import { t } from "i18next";
import { MediaProjectResponse } from "pages/media/types";
import { useCallback, useMemo } from "react";
import { useWsSubscription } from "hooks/useWsSubscription";
import { WSEvent } from "store/websocketStore";
import { CustomAxiosError } from "services/api/";

export const useProjectDetails = ({ projectId }: { projectId?: string }) => {
  const queryResult = useOne<MediaProjectResponse>({
    resource: "media/projects",
    id: projectId,
    errorNotification: (error) => {
      const status = (error as CustomAxiosError)?.statusCode;
      if (status === 403) return false;
      return {
        type: "error",
        message: t("media.projects.detail.errorLoadingProject"),
        description: t("media.projects.detail.pleaseTryAgain"),
      };
    },
    queryOptions: {
      enabled: !!projectId,
    },
  });

  const subscriptionConfig = useMemo(
    () => (projectId ? { type: "project" as const, id: projectId } : []),
    [projectId]
  );

  const { refetch } = queryResult;

  const handleStatusChange = useCallback(
    (event: WSEvent) => {
      if (event.project_id === projectId) {
        refetch().catch((error) => {
          console.error(
            "[useProjectDetails] error refetching project details after status change",
            error
          );
        });
      }
    },
    [projectId, refetch]
  );

  useWsSubscription(subscriptionConfig, handleStatusChange);

  const isAccessDenied =
    queryResult.isError &&
    (queryResult.error as unknown as { response?: { status?: number } })
      ?.response?.status === 403;

  return {
    ...queryResult,
    isAccessDenied,
    error: queryResult.error,
  };
};
